import React, { useState, useEffect, startTransition } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    CircularProgress,
    Box,
    Typography
} from '@mui/material';
import clientService from 'src/clientService';
import { useSnackbar } from 'src/view/components/SnackbarService';
import { useAppState } from 'src/state-management/state-provider';
import { Project, ProjectAdminSummary } from 'src/api/Api';

interface AssignProjectDialogProps {
    open: boolean;
    onClose: () => void;
    onProjectAssigned: () => void;
    userId: string;
    userEmail: string;
    assignedProjectIds: string[];
}

const AssignProjectDialog: React.FC<AssignProjectDialogProps> = ({ open, onClose, onProjectAssigned, userId, userEmail, assignedProjectIds }) => {
    const { state, dispatch } = useAppState();
    const [externalProjects, setExternalProjects] = useState<Project[]>([]);
    const [selectedProject, setSelectedProject] = useState<string>('');
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);
    const [isAssigning, setIsAssigning] = useState<boolean>(false);
    const { showSnackbar, snackbarType } = useSnackbar();

    const isDarukaaUser = userEmail.endsWith('@darukaa.com');
    const projectsToShow = (isDarukaaUser ? state.admin.projects : externalProjects) as (Project | ProjectAdminSummary)[];

    useEffect(() => {
        if (!open) return;

        const fetchProjects = async () => {
            setLoading(true);
            setError(null);
            try {
                if (isDarukaaUser) {
                    if (state.admin.projects.length === 0) {
                        const response = await clientService.api.getAllProjectsAdminApiV1AdminProjectsGet();
                        startTransition(() => {
                            if (response && response.data) {
                                dispatch({ type: 'ADMIN_SET_PROJECTS', payload: response.data });
                            }
                        });
                    }
                } else {
                    const response = await clientService.api.getProjectsByOrgEmailApiV1AssignmentsProjectsByOrgEmailOrgEmailGet(userEmail);
                    startTransition(() => {
                        if (response && response.data) {
                            setExternalProjects(response.data);
                        }
                    });
                }
            } catch (err) {
                console.error('Failed to fetch projects:', err);
                setError('Failed to fetch projects.');
            } finally {
                setLoading(false);
            }
        };

        if (isDarukaaUser) {
            if (state.admin.projects.length === 0) fetchProjects();
        } else {
            fetchProjects();
        }
    }, [open, userEmail, isDarukaaUser, state.admin.projects.length, dispatch]);

    const handleAssign = async () => {
        if (!selectedProject) {
            showSnackbar({ message: 'Please select a project.', type: snackbarType.WARNING });
            return;
        }
        setIsAssigning(true);
        try {
            await clientService.api.createAssignmentEndpointApiV1AssignmentsPost({ project_id: selectedProject, user_id: userId });
            showSnackbar({ message: 'Project assigned successfully.', type: snackbarType.SUCCESS });
            onProjectAssigned();
            onClose();
        } catch (error: any) {
            const errorMessage = error?.detail?.[0]?.msg || error?.message || 'Failed to assign project.';
            showSnackbar({ message: `Error: ${errorMessage}`, type: snackbarType.ERROR });
        } finally {
            setIsAssigning(false);
        }
    };

    const unassignedProjects = projectsToShow.filter((p: Project | ProjectAdminSummary) => !assignedProjectIds.includes(p.project_id));

    if (loading) {
        return (
            <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
                <DialogTitle>Assign Project</DialogTitle>
                <DialogContent>
                    <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}><CircularProgress /></Box>
                </DialogContent>
            </Dialog>
        );
    }

    if (error) {
        return (
            <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
                <DialogTitle>Assign Project</DialogTitle>
                <DialogContent>
                    <Typography color="error">{error}</Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={onClose}>Cancel</Button>
                </DialogActions>
            </Dialog>
        );
    }

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
            <DialogTitle>Assign Project</DialogTitle>
            <DialogContent>
                <FormControl fullWidth sx={{ mt: 2 }}>
                    <InputLabel id="project-select-label">Project</InputLabel>
                    <Select
                        labelId="project-select-label"
                        value={selectedProject}
                        onChange={(e) => setSelectedProject(e.target.value as string)}
                        label="Project"
                    >
                        {unassignedProjects.length > 0 ? (
                            unassignedProjects.map((project) => (
                                <MenuItem key={project.project_id} value={project.project_id}>
                                    {project.project_name}
                                </MenuItem>
                            ))
                        ) : (
                            <MenuItem disabled>No unassigned projects available</MenuItem>
                        )}
                    </Select>
                </FormControl>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={isAssigning}>Cancel</Button>
                <Button onClick={handleAssign} variant="contained" disabled={!selectedProject || isAssigning}>
                    {isAssigning ? <CircularProgress size={24} /> : 'Assign'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default AssignProjectDialog;
